<template>
  <el-dialog
    v-dialog-window="resizeDialogMap"
    title="地图选点"
    :visible.sync="dialogVisible"
    width="720px"
    top="4vh"
    custom-class="waybill-location-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <div ref="mapPrewarmWrap" class="map-prewarm" aria-hidden="true">
      <div ref="mapSingletonEl" class="map-el"></div>
    </div>
    <div class="map-search">
      <el-autocomplete
        v-model="mapSearchText"
        :fetch-suggestions="querySearchAsync"
        placeholder="输入位置"
        size="small"
        class="map-search__place"
        popper-append-to-body
        :trigger-on-focus="false"
        @select="handleSelect"
        @keyup.enter.native="searchLocation"
      >
        <template slot-scope="{ item }"><div>{{ item.name }}</div><small>{{ item.context }}</small></template>
        <el-button slot="append" icon="el-icon-search" @click="searchLocation" />
      </el-autocomplete>
      <el-input v-model.trim="coordinateText" size="small" class="map-search__coord" placeholder="经度,纬度" @keyup.enter.native="locateByCoordinate" />
      <el-button size="small" icon="el-icon-aim" @click="locateByCoordinate">定位</el-button>
    </div>
    <div ref="mapDialogSlot" class="map-slot"></div>
    <div class="picked-result">
      <span>已选坐标：{{ pickedCoordinateText || '请在地图上点击选点' }}</span>
      <span v-if="pickedAddress">地址：{{ pickedAddress }}</span>
    </div>
    <span slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="resolvingAddress" :disabled="!pickedLonLat || resolvingAddress" @click="confirm">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { setAmapSecurityConfig, loadAmapSDK } from '@/utils/amapConfig'
import { buildFullAddress, getAreaCodesByAdcode, getAreaName, naturalAreaName, stripAreaPrefix } from './locationArea'
import { queryLocationSuggestions, reverseLocation, locationPoint } from './waybillLocation'
import { formatLonLat, parseLonLat, gcj02PointToWgs84LonLat, wgs84LonLatToGcj02Point } from './coordinate'

/* global AMap */
export default {
  name: 'WaybillLocationPicker',
  props: {
    visible: { type: Boolean, default: false },
    lonLat: { type: String, default: '' },
    address: { type: String, default: '' },
    areaCodes: { type: Array, default: () => [] },
    areaName: { type: String, default: '' }
  },
  data() {
    return {
      dialogVisible: false,
      mapSearchText: '',
      coordinateText: '',
      pickedLonLat: '',
      pickedAddress: '',
      pickedAreaCodes: [],
      resolvingAddress: false,
      locationRequestSequence: 0,
      suggestionRequestSequence: 0,
      map: null,
      pickerMapInitPromise: null
    }
  },
  computed: {
    pickedCoordinateText() { return formatLonLat(wgs84LonLatToGcj02Point(this.pickedLonLat)) }
  },
  watch: {
    visible(v) {
      const requestId = ++this.locationRequestSequence
      this.suggestionRequestSequence++
      this.dialogVisible = v
      if (v) {
        this.pickedLonLat = this.lonLat || ''
        this.pickedAddress = this.address || ''
        this.pickedAreaCodes = Array.isArray(this.areaCodes) ? this.areaCodes.slice() : []
        this.resolvingAddress = true
        this.mapSearchText = this.getSearchAddress()
        this.coordinateText = this.pickedCoordinateText
        this.$nextTick(() => this.openMap(requestId))
      }
    },
    dialogVisible(v) {
      if (!v) {
        this.locationRequestSequence++
        this.suggestionRequestSequence++
        this.resolvingAddress = false
      }
      this.$emit('update:visible', v)
    }
  },
  beforeDestroy() {
    this.locationRequestSequence++
    this.suggestionRequestSequence++
    if (this.map) this.map.destroy()
  },
  methods: {
    resizeDialogMap() { if (this.map) this.map.resize() },
    async openMap(requestId = this.locationRequestSequence) {
      try {
        await this.ensurePickerMapLoaded()
        if (requestId !== this.locationRequestSequence) return
        const slot = this.$refs.mapDialogSlot
        const el = this.$refs.mapSingletonEl
        if (slot && el && el.parentNode !== slot) slot.appendChild(el)
        this.refreshMapMarker()
        const point = wgs84LonLatToGcj02Point(this.pickedLonLat)
        if (point) await this.resolvePickedAddress(point[0], point[1], requestId, true)
        else await this.locateAddressOrArea(requestId)
        await this.$nextTick()
        if (requestId !== this.locationRequestSequence) return
        this.map.resize()
      } catch (e) {
        if (requestId === this.locationRequestSequence) {
          this.resolvingAddress = false
          this.$message.error('地图加载失败，请稍后重试')
        }
      }
    },
    async ensurePickerMapLoaded() {
      if (this.map) return
      if (!this.pickerMapInitPromise) {
        this.pickerMapInitPromise = this.createPickerMap().catch((e) => {
          this.pickerMapInitPromise = null
          throw e
        })
      }
      await this.pickerMapInitPromise
    },
    async createPickerMap() {
      setAmapSecurityConfig()
      await loadAmapSDK(['AMap.Geocoder', 'AMap.AutoComplete', 'AMap.ToolBar'])
      const center = wgs84LonLatToGcj02Point(this.pickedLonLat) || [116.404, 39.915]
      this.map = new AMap.Map(this.$refs.mapSingletonEl, {
        zoom: this.pickedLonLat ? 14 : 11,
        center,
        resizeEnable: true
      })
      this.map.addControl(new AMap.ToolBar())
      this.map.on('click', (e) => this.pick(e.lnglat.getLng(), e.lnglat.getLat()))
    },
    refreshMapMarker() {
      if (!this.map) return
      this.map.clearMap()
      const point = wgs84LonLatToGcj02Point(this.pickedLonLat)
      if (point) {
        this.map.setCenter(point)
        new AMap.Marker({ position: point, map: this.map })
      }
    },
    geocode(address) {
      if (!address || !window.AMap) return Promise.resolve(null)
      return new Promise((resolve) => {
        new AMap.Geocoder().getLocation(address, (status, result) => {
          if (status !== 'complete' || !result.geocodes || !result.geocodes.length) return resolve(null)
          const location = result.geocodes[0].location
          resolve([location.getLng(), location.getLat()])
        })
      })
    },
    async setPickedPoint(lng, lat, requestId = ++this.locationRequestSequence, preserveAddress = false) {
      const wgs84LonLat = gcj02PointToWgs84LonLat(lng, lat)
      if (!wgs84LonLat) return
      this.pickedLonLat = wgs84LonLat
      this.coordinateText = formatLonLat([Number(lng), Number(lat)])
      if (!preserveAddress) this.pickedAddress = ''
      this.pickedAreaCodes = []
      this.refreshMapMarker()
      await this.resolvePickedAddress(lng, lat, requestId, preserveAddress)
    },
    async resolvePickedAddress(lng, lat, requestId, preserveAddress) {
      this.resolvingAddress = true
      try {
        const result = await this.reverseGeocode(lng, lat)
        if (requestId !== this.locationRequestSequence) return
        if (!result || result.areaCodes.length !== 3) {
          this.$message.warning('未获取到该位置的省市区，请重新选点或稍后重试')
          return
        }
        this.pickedAreaCodes = result.areaCodes
        if (result.address && (!preserveAddress || !this.pickedAddress)) this.pickedAddress = result.address
      } catch (e) {
        if (requestId === this.locationRequestSequence) this.$message.warning('省市区解析失败，请重新选点或稍后重试')
      } finally {
        if (requestId === this.locationRequestSequence) this.resolvingAddress = false
      }
    },
    async locateAddressOrArea(requestId = this.locationRequestSequence) {
      if (!this.map) return
      const address = this.getSearchAddress()
      if (!address) { this.resolvingAddress = false; return }
      const point = await this.geocode(address)
      if (requestId !== this.locationRequestSequence) return
      if (!point) {
        this.resolvingAddress = false
        this.$message.warning('未找到该地点，请在地图上选择精确位置')
        return
      }
      this.map.setCenter(point)
      this.map.setZoom(14)
      // 仅选择地区时只调整地图视野，不能把地区中心当成装卸点。
      if (!this.pickedAddress) { this.resolvingAddress = false; return }
      await this.setPickedPoint(point[0], point[1], requestId, true)
    },
    getSearchAddress() {
      return getAreaName(this.pickedAreaCodes)
        ? buildFullAddress(this.pickedAreaCodes, this.pickedAddress)
        : `${naturalAreaName(this.areaName)}${stripAreaPrefix(this.pickedAddress, this.areaName)}`
    },
    pick(lng, lat) {
      return this.setPickedPoint(lng, lat)
    },
    reverseGeocode(lng, lat) { return reverseLocation(window.AMap, lng, lat) },
    getAreaCodes(component) {
      return getAreaCodesByAdcode(component.adcode, component)
    },
    getAreaName(codes) { return getAreaName(codes) },
    querySearchAsync(keyword, callback) {
      const requestId = ++this.suggestionRequestSequence
      const locationRequestId = this.locationRequestSequence
      queryLocationSuggestions(window.AMap, keyword, items => {
        if (requestId === this.suggestionRequestSequence && locationRequestId === this.locationRequestSequence) callback(items)
      })
    },
    handleSelect(item) {
      if (!item || !item.location) return
      const point = locationPoint(item.location)
      if (point) {
        this.map.setZoom(15)
        this.pick(point[0], point[1])
      }
    },
    async searchLocation() {
      if (!this.mapSearchText || !window.AMap) return
      const requestId = ++this.locationRequestSequence
      this.resolvingAddress = true
      try {
        const point = await this.geocode(this.mapSearchText)
        if (requestId !== this.locationRequestSequence) return
        if (point) {
          this.map.setCenter(point)
          this.map.setZoom(15)
          await this.setPickedPoint(point[0], point[1], requestId)
        } else this.$message.warning('未找到该地点')
      } catch (e) {
        if (requestId === this.locationRequestSequence) this.$message.warning('地点搜索失败，请稍后重试')
      } finally {
        if (requestId === this.locationRequestSequence) this.resolvingAddress = false
      }
    },
    locateByCoordinate() {
      const point = parseLonLat(this.coordinateText)
      if (!point) {
        this.$message.warning('请输入有效经纬度，格式：经度,纬度')
        return
      }
      this.map.setCenter(point)
      this.map.setZoom(15)
      // 回显坐标未变时保留精确原值，避免反复定位产生往返转换漂移。
      if (formatLonLat(point) === this.pickedCoordinateText) {
        if (this.resolvingAddress || this.pickedAreaCodes.length !== 3) return this.resolvePickedAddress(point[0], point[1], ++this.locationRequestSequence, true)
        return
      }
      return this.setPickedPoint(point[0], point[1])
    },
    confirm() {
      if (this.resolvingAddress) return
      if (!parseLonLat(this.pickedLonLat) || this.pickedAreaCodes.length !== 3) {
        this.$message.warning('请先选择精确位置并获取省市区')
        return
      }
      this.$emit('confirm', { lonLat: this.pickedLonLat, address: this.pickedAddress, areaCodes: this.pickedAreaCodes, areaName: this.getAreaName(this.pickedAreaCodes) })
      this.dialogVisible = false
    },
    onClosed() {
      if (this.dialogVisible) return
      this.restoreMapContainer()
    },
    restoreMapContainer() {
      const wrap = this.$refs.mapPrewarmWrap
      const el = this.$refs.mapSingletonEl
      if (this.map && wrap && el && el.parentNode !== wrap) {
        wrap.appendChild(el)
        this.$nextTick(() => this.map && this.map.resize())
      }
    }
  }
}
</script>

<style>
.map-prewarm { position: fixed; top: 0; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.map-el, .map-slot { width: 100%; height: 340px; }
.map-search { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.map-search__place { width: 270px; }
.map-search__coord { width: 150px; }
.map-slot { border: 1px solid #e4e7ed; }
.picked-result { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; color: #606266; font-size: 13px; }
.waybill-location-dialog .el-dialog__body { padding: 14px 20px 10px; }
.waybill-location-dialog.dialog-window > .el-dialog__body { display: flex; flex-direction: column; overflow: auto; }
.waybill-location-dialog.dialog-window .map-slot { min-height: 120px; height: auto; flex: 1; }
.waybill-location-dialog.dialog-window .map-slot > .map-el { height: 100%; }
.waybill-location-dialog.dialog-window .map-search, .waybill-location-dialog.dialog-window .picked-result { flex: none; }
@media (max-height: 760px) { .map-el, .map-slot { height: 300px; } }
@media (max-width: 760px) { .waybill-location-dialog:not(.dialog-window) { width: calc(100vw - 24px) !important; } .map-search { flex-wrap: wrap; } }
</style>
